import type { ReactNode } from 'react';
import { Alert, Button, Card, Heading, Text } from '@syia-ai/maritimeai-ui';
import { LinkButton } from '@/components/ui';

/**
 * The full-page card every "you can't see the page you asked for" state uses — access
 * denied, an app that isn't live, a failed permissions or session check, a 404, a crash.
 *
 * These were seven separate hand-styled blocks that had already drifted apart in heading
 * size, card width, radius, shadow and logo size. One component keeps them one product and
 * means a change lands everywhere at once. Built from Maritime Card, Heading, Text, Alert and
 * Button; a link action is a LinkButton stand-in (see components/ui).
 */

export interface StatusCardAction {
  label: string;
  /** Navigations use href so they behave like links (middle-click, open in new tab). */
  href?: string;
  /**
   * The action for a button. On a link this is a side effect fired as the user leaves —
   * recording that the attempt happened, say — not the navigation itself, which stays
   * the browser's job. Keep it synchronous: the page is about to unload.
   */
  onClick?: () => void;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
}

export interface StatusCardProps {
  title: string;
  /** Body copy. Use <br /> for a deliberate line break. */
  message: ReactNode;
  /** The bordered box under the message — help text, or a go-live estimate. */
  note?: {
    title: string;
    body: ReactNode;
    /** 'amber' marks a scheduled return rather than a problem. */
    tone?: 'neutral' | 'amber';
  };
  actions: StatusCardAction[];
}

export default function StatusCard({ title, message, note, actions }: StatusCardProps) {
  return (
    <div className="shell-status">
      <Card as="section" variant="raised" padding="lg" className="shell-status__card" aria-labelledby="shell-status-title">
        <img src="/siya.png" alt="SIYA" width={64} height={64} className="shell-status__logo" />

        <Heading level={1} size="lg" id="shell-status-title" className="shell-status__title">
          {title}
        </Heading>

        <Text tone="muted" className="shell-status__message">
          {message}
        </Text>

        {note && (
          // 'amber' is a scheduled return, not a failure — a warning, not a danger.
          <Alert variant={note.tone === 'amber' ? 'warning' : 'info'} title={note.title} role="note">
            {note.body}
          </Alert>
        )}

        {actions.length > 0 && (
          <div className="shell-status__actions">
            {actions.map((action) => {
              const variant = action.variant ?? 'primary';

              if (action.href) {
                return (
                  <LinkButton
                    key={action.label}
                    href={action.href}
                    onClick={action.onClick}
                    // Break out of the chat's Micro Apps panel. These cards render inside
                    // that iframe, and signing in there would load the whole login page into
                    // a small panel — and land the user back inside it afterwards. _top is a
                    // no-op when not framed.
                    target="_top"
                    variant={variant}
                  >
                    {action.label}
                  </LinkButton>
                );
              }

              return (
                <Button key={action.label} variant={variant} onClick={action.onClick} disabled={action.disabled}>
                  {action.label}
                </Button>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
